/**
 * Boots the app: loads config, creates the Supabase client, resolves the
 * session, then renders. Everything below this point can assume config and
 * client exist, which keeps null checks out of every component.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { loadConfig, type PublicConfig } from "../lib/config";
import { createBrowserClient } from "../lib/supabase";
import { createApiClient, createDownloadClient, type ApiClient, type DownloadClient } from "../lib/api";

interface SessionContextValue {
  config: PublicConfig;
  supabase: SupabaseClient;
  api: ApiClient;
  download: DownloadClient;
  session: Session | null;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

type BootState =
  | { phase: "loading" }
  | { phase: "failed"; message: string }
  | { phase: "ready"; config: PublicConfig; supabase: SupabaseClient };

export function SessionProvider({ children }: { children: ReactNode }) {
  const [boot, setBoot] = useState<BootState>({ phase: "loading" });
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    let active = true;

    loadConfig()
      .then((config) => {
        if (!active) return;
        const supabase = createBrowserClient(config);
        setBoot({ phase: "ready", config, supabase });
      })
      .catch((error: unknown) => {
        if (!active) return;
        setBoot({
          phase: "failed",
          message: error instanceof Error ? error.message : "Could not start the app.",
        });
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (boot.phase !== "ready") return;

    const { supabase } = boot;
    let active = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (active) setSession(data.session);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [boot]);

  const value = useMemo<SessionContextValue | null>(() => {
    if (boot.phase !== "ready") return null;
    return {
      config: boot.config,
      supabase: boot.supabase,
      api: createApiClient(boot.supabase),
      download: createDownloadClient(boot.supabase),
      session,
      signOut: async () => {
        await boot.supabase.auth.signOut();
      },
    };
  }, [boot, session]);

  if (boot.phase === "loading") {
    return <BootScreen>Starting Cubby</BootScreen>;
  }

  if (boot.phase === "failed") {
    return (
      <BootScreen>
        <span className="text-red-700">{boot.message}</span>
      </BootScreen>
    );
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

function BootScreen({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-stone-50 px-6">
      <p className="text-sm text-stone-600">{children}</p>
    </div>
  );
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error("useSession must be used inside SessionProvider");
  }
  return value;
}
